// HelpAsk — "Ask Audrey where things are" on the Help & Training page
// (ticket 212). Audrey answers from the training lessons and the portal page
// map (backend portal_map.py). Only paths the server returned in `pages`
// (checked against the map) become links; anything else stays plain text.
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Loader2, ChevronRight } from 'lucide-react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { api } from '../api';

const MAX_LEN = 500;
const PORTAL_LINK = /\[([^\]]+)\]\((\/portal[^)\s]*)\)/g;

/** The answer text with its [Page](/portal/path) links as router links. A link
 *  whose path is not one of the server-checked `pages` shows as its text only. */
function AnswerText({ text, pages }) {
  const known = new Set((pages || []).map((p) => p.path));
  const plain = text.replace(/\*\*/g, '');
  const parts = [];
  let last = 0;
  for (const m of plain.matchAll(PORTAL_LINK)) {
    if (m.index > last) parts.push(plain.slice(last, m.index));
    const path = m[2].replace(/[?#].*$/, '').replace(/(.)\/$/, '$1');
    parts.push(known.has(path)
      ? <Link key={m.index} to={path} className="font-medium underline text-[#070B5F] hover:text-[#6BA82E]">{m[1]}</Link>
      : m[1]);
    last = m.index + m[0].length;
  }
  if (last < plain.length) parts.push(plain.slice(last));
  return <p className="text-sm text-slate-700 whitespace-pre-line" data-testid="help-ask-answer">{parts}</p>;
}

export default function HelpAsk() {
  const [question, setQuestion] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const ask = async (e) => {
    e.preventDefault();
    const q = question.trim();
    if (!q || busy) return;
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post('/v2/training/ask', { question: q });
      setResult(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      // A 422 `detail` is a pydantic error list and a spend 403 is an object; only show strings.
      setError(typeof detail === 'string' ? detail : 'Audrey could not answer just now. Try again in a minute.');
      setResult(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-4 md:p-5 mb-6" data-testid="help-ask">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles size={16} className="text-[#6BA82E]" />
        <div className="font-semibold text-sm text-slate-900">Can't find something? Ask Audrey where it is</div>
      </div>
      <p className="text-xs text-slate-600 mb-3">
        She answers from these lessons and a map of the portal, and links you to the page.
      </p>
      <form onSubmit={ask} className="flex flex-col sm:flex-row gap-2">
        <Input
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          maxLength={MAX_LEN}
          placeholder="e.g. Where do I invite a new loan officer?"
          aria-label="Ask Audrey where something is"
          data-testid="help-ask-input"
        />
        <Button type="submit" disabled={busy || !question.trim()} data-testid="help-ask-submit">
          {busy ? <Loader2 size={14} className="animate-spin" /> : 'Ask'}
        </Button>
      </form>
      {error && <p className="text-sm text-red-600 mt-3" role="alert" data-testid="help-ask-error">{error}</p>}
      {result && (
        <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 p-3 space-y-2">
          <AnswerText text={result.answer} pages={result.pages} />
          {result.pages?.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {result.pages.map((p) => (
                <Button key={p.path} asChild variant="outline" size="sm">
                  <Link to={p.path} data-testid={`help-ask-page-${p.path}`}>
                    Open {p.name} <ChevronRight size={14} className="ml-1" />
                  </Link>
                </Button>
              ))}
            </div>
          )}
          <p className="text-[11px] text-slate-500">AI-generated. If that's not it, send us feedback below.</p>
        </div>
      )}
    </Card>
  );
}
